import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Heart, MapPin, HandHeart, Newspaper, FileText, Video, Users, TrendingUp, Award, Package, ChevronDown, X, Menu, School, ClipboardList } from 'lucide-react';
import arcaLogo from '../../assets/arca_logo.png';
import LanguageTranslator from '../common/LanguageTranslator';

const SecondaryNavbar = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const handleMouseEnter = (menu) => {
    setActiveDropdown(menu);
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (!mobileMenuOpen) {
      setMobileDropdown(null);
    }
  };

  const toggleMobileDropdown = (menu) => {
    setMobileDropdown(mobileDropdown === menu ? null : menu);
  };

  // Menu items for mobile
  const mobileMenuItems = [
    {
      title: 'Programs',
      icon: GraduationCap,
      items: [
        { title: 'Education & Training', link: '/programs/education-training', icon: GraduationCap, description: 'Professional development programs' },
        { title: 'Research & Innovation', link: '/programs/research-innovation', icon: TrendingUp, description: 'Advancing respiratory care research' },
        { title: 'Advocacy & Awareness', link: '/programs/advocacy-awareness', icon: Heart, description: 'Policy and public awareness' }
      ]
    },
    { title: 'ARCA Virtual University', link: '/university', icon: GraduationCap },
    { title: 'Community', link: '/community', icon: Users },
    {
      title: 'Impact',
      icon: Award,
      items: [
        { title: 'Success Stories', link: '/impact/success-stories', icon: Award, description: 'Inspiring impact stories' },
        { title: 'Annual Reports', link: '/impact/annual-reports', icon: FileText, description: 'Financial & program reports' }
      ]
    },
    { title: 'Chapters', link: '/chapters', icon: MapPin },
    { title: 'RT Institutions', link: '/institutions', icon: School },
    {
      title: 'Get Involved',
      icon: HandHeart,
      items: [
        { title: 'Become a Member', link: '/get-involved/become-member', icon: Users, description: 'Join our community' },
        { title: 'Volunteer / Internship', link: '/get-involved/volunteer', icon: HandHeart, description: 'Make a difference' },
        { title: 'Partner with ARCA', link: '/get-involved/partner', icon: MapPin, description: 'Strategic collaborations' },
        { title: 'Donate', link: '/get-involved/donate', icon: Heart, description: 'Support our mission' }
      ]
    },
    { title: 'Stories / News', link: '/news', icon: Newspaper },
    {
      title: 'Resources',
      icon: FileText,
      items: [
        { title: 'Publications & Reports', link: '/resources/publications', icon: FileText, description: 'Research papers & guidelines' },
        { title: 'Training Materials', link: '/resources/training-materials', icon: GraduationCap, description: 'Educational resources & curricula' },
        { title: 'Policy Documents', link: '/resources/policy-documents', icon: FileText, description: 'Guidelines & policy papers' },
        { title: 'Policy Developments', link: '/resources/policy-developments', icon: TrendingUp, description: 'Latest policy updates & advocacy' },
        { title: 'Clinical Guidelines', link: '/resources/clinical-guidelines', icon: Heart, description: 'Best practices & protocols' },
        { title: 'FAQs', link: '/resources/faqs', icon: Newspaper, description: 'Frequently asked questions' }
      ]
    },
    {
      title: 'Media',
      icon: Video,
      items: [
        { title: 'YouTube Videos', link: '/media/youtube-videos', icon: Video, description: 'Educational videos and webinars' },
        { title: 'Photo & Event Gallery', link: '/media/photo-gallery', icon: Video, description: 'Conference and event photos' },
        { title: 'Press Releases', link: '/media/press-releases', icon: Newspaper, description: 'Official announcements' },
        { title: 'Media Kit', link: '/media/media-kit', icon: FileText, description: 'Logos, fact sheets, and resources' }
      ]
    },
    { title: 'Surveys', link: '/surveys', icon: ClipboardList }
  ];

  return (
    <nav className="navbar navbar-expand-lg navbar-dark secondary-navbar shadow-sm sticky-top" style={{ minHeight: '56px' }}>
      <div className="container-fluid px-4">
        <div className="d-flex justify-content-between align-items-center w-100">
          {/* Desktop Navigation Menu */}
          <div className="d-none d-lg-flex align-items-center flex-grow-1 justify-content-between">
            {/* Programs - Dropdown */}
            <div
              className="dropdown me-2"
              onMouseEnter={() => handleMouseEnter('programs')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="btn btn-link text-white px-2 py-2 d-flex align-items-center"
                style={{ textDecoration: 'none', border: 'none', fontWeight: '500', fontSize: '0.813rem' }}
              >
                Programs
                <span className="ms-1" style={{ fontSize: '0.7rem' }}>▼</span>
              </button>

              {activeDropdown === 'programs' && (
                <div className="dropdown-menu mega-menu show position-absolute shadow-lg border-0" style={{ top: '100%', left: '0', zIndex: 1050, minWidth: '600px', borderRadius: '8px' }}>
                  <div className="container p-4">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <Link to="/programs/education-training" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <GraduationCap className="text-primary me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Education & Training</div>
                              <small className="text-muted">Professional development programs</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/programs/research-innovation" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <TrendingUp className="text-success me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Research & Innovation</div>
                              <small className="text-muted">Advancing respiratory care research</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/programs/advocacy-awareness" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <Heart className="text-danger me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Advocacy & Awareness</div>
                              <small className="text-muted">Policy and public awareness</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ARCA Virtual University */}
            <Link
              to="/university"
              className="nav-link text-white px-2 py-2 me-2"
              style={{
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '0.813rem',
                background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)',
                padding: '8px 12px',
                borderRadius: '4px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #7BA52A 0%, #6B8E23 100%)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              ARCA Virtual University
            </Link>

            {/* ARCA Community */}
            <Link
              to="/community"
              className="btn btn-sm text-white fw-semibold me-2 rounded-pill shadow-sm text-decoration-none"
              style={{
                backgroundColor: '#6B8E23',
                borderColor: '#6B8E23',
                fontSize: '0.813rem',
                padding: '0.5rem 1rem',
                transition: 'all 0.3s ease',
                border: '2px solid #6B8E23'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#58761C';
                e.currentTarget.style.borderColor = '#58761C';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 12px rgba(107, 142, 35, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#6B8E23';
                e.currentTarget.style.borderColor = '#6B8E23';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.12)';
              }}
            >
              <i className="fas fa-users me-2"></i>
              Community
            </Link>

            {/* Impact - Dropdown */}
            <div
              className="dropdown me-2"
              onMouseEnter={() => handleMouseEnter('impact')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="btn btn-link text-white px-2 py-2 d-flex align-items-center"
                style={{ textDecoration: 'none', border: 'none', fontWeight: '500', fontSize: '0.813rem' }}
              >
                Impact
                <span className="ms-1" style={{ fontSize: '0.7rem' }}>▼</span>
              </button>

              {activeDropdown === 'impact' && (
                <div className="dropdown-menu mega-menu show position-absolute shadow-lg border-0" style={{ top: '100%', left: '-100px', zIndex: 1050, width: '600px', borderRadius: '8px', backgroundColor: '#fff', padding: '1.5rem' }}>
                  <div className="container-fluid">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <Link to="/impact/success-stories" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <Award className="text-warning me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Success Stories</div>
                              <small className="text-muted">Inspiring impact stories</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/impact/annual-reports" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <FileText className="text-primary me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Annual Reports</div>
                              <small className="text-muted">Financial & program reports</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chapters */}
            <Link
              to="/chapters"
              className="nav-link text-white px-2 py-2 me-2"
              style={{ textDecoration: 'none', fontWeight: '500', fontSize: '0.813rem' }}
            >
              Chapters
            </Link>

            {/* RT Institutions */}
            <Link
              to="/institutions"
              className="nav-link text-white px-2 py-2 me-2"
              style={{ textDecoration: 'none', fontWeight: '500', fontSize: '0.813rem' }}
            >
              RT Institutions
            </Link>

            {/* Get Involved - Dropdown */}
            <div
              className="dropdown me-2"
              onMouseEnter={() => handleMouseEnter('getInvolved')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="btn btn-link text-white px-2 py-2 d-flex align-items-center"
                style={{ textDecoration: 'none', border: 'none', fontWeight: '500', fontSize: '0.813rem' }}
              >
                Get Involved
                <span className="ms-1" style={{ fontSize: '0.7rem' }}>▼</span>
              </button>

              {activeDropdown === 'getInvolved' && (
                <div className="dropdown-menu mega-menu show position-absolute shadow-lg border-0" style={{ top: '100%', left: '0', zIndex: 1050, minWidth: '500px', borderRadius: '8px' }}>
                  <div className="container p-4">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <Link to="/get-involved/become-member" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <Users className="text-primary me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Become a Member</div>
                              <small className="text-muted">Join our community</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/get-involved/volunteer" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <HandHeart className="text-success me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Volunteer / Internship</div>
                              <small className="text-muted">Make a difference</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/get-involved/partner" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <MapPin className="text-warning me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Partner with ARCA</div>
                              <small className="text-muted">Strategic collaborations</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link
                          to="/get-involved/donate"
                          className="dropdown-item d-flex align-items-start p-3 rounded"
                          style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div className="d-flex align-items-start">
                            <Heart className="text-danger me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Donate</div>
                              <small className="text-muted">Support our mission</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Stories / News */}
            <Link
              to="/news"
              className="nav-link text-white px-2 py-2 me-2"
              style={{ textDecoration: 'none', fontWeight: '500', fontSize: '0.813rem' }}
            >
              Stories / News
            </Link>

            {/* Resources - Dropdown */}
            <div
              className="dropdown me-2"
              onMouseEnter={() => handleMouseEnter('resources')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="btn btn-link text-white px-2 py-2 d-flex align-items-center"
                style={{ textDecoration: 'none', border: 'none', fontWeight: '500', fontSize: '0.813rem' }}
              >
                Resources
                <span className="ms-1" style={{ fontSize: '0.7rem' }}>▼</span>
              </button>

              {activeDropdown === 'resources' && (
                <div className="dropdown-menu mega-menu show position-absolute shadow-lg border-0" style={{ top: '100%', left: '0', zIndex: 1050, minWidth: '600px', borderRadius: '8px' }}>
                  <div className="p-4">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <Link to="/resources/publications" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <FileText className="text-primary me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Publications & Reports</div>
                              <small className="text-muted">Research papers & guidelines</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/resources/training-materials" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <GraduationCap className="text-success me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Training Materials</div>
                              <small className="text-muted">Educational resources & curricula</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/resources/policy-documents" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <FileText className="text-info me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Policy Documents</div>
                              <small className="text-muted">Guidelines & policy papers</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/resources/policy-developments" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <TrendingUp className="text-warning me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Policy Developments</div>
                              <small className="text-muted">Latest policy updates & advocacy</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/resources/clinical-guidelines" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <Heart className="text-danger me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Clinical Guidelines</div>
                              <small className="text-muted">Best practices & protocols</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/resources/faqs" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <Newspaper className="text-secondary me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">FAQs</div>
                              <small className="text-muted">Frequently asked questions</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Media - Dropdown */}
            <div
              className="dropdown me-2"
              onMouseEnter={() => handleMouseEnter('media')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="btn btn-link text-white px-2 py-2 d-flex align-items-center"
                style={{ textDecoration: 'none', border: 'none', fontWeight: '500', fontSize: '0.813rem' }}
              >
                Media
                <span className="ms-1" style={{ fontSize: '0.7rem' }}>▼</span>
              </button>

              {activeDropdown === 'media' && (
                <div className="dropdown-menu mega-menu show position-absolute shadow-lg border-0" style={{ top: '100%', left: '0', zIndex: 1050, minWidth: '450px', maxWidth: '450px', borderRadius: '8px' }}>
                  <div className="p-4">
                    <div className="row g-3">
                      <div className="col-12">
                        <Link to="/media/youtube-videos" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <Video className="text-danger me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">YouTube Videos</div>
                              <small className="text-muted">Educational videos and webinars</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-12">
                        <Link to="/media/photo-gallery" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none', whiteSpace: 'nowrap' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start" style={{ overflow: 'hidden' }}>
                            <Video className="text-primary me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Photo & Event Gallery</div>
                              <small className="text-muted">Conference and event photos</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-12">
                        <Link to="/media/press-releases" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <Newspaper className="text-success me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Press Releases</div>
                              <small className="text-muted">Official announcements</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-12">
                        <Link to="/media/media-kit" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <FileText className="text-info me-3 mt-1 flex-shrink-0" size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Media Kit</div>
                              <small className="text-muted">Logos, fact sheets, and resources</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Surveys */}
            <Link
              to="/surveys"
              className="nav-link px-3 py-2 me-2"
              style={{
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '0.813rem',
                color: '#ffffff',
                backgroundColor: '#dc3545',
                borderRadius: '6px',
                border: '1px solid #dc3545',
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
              }}
            >
              Surveys
            </Link>
          </div>

          {/* Language Translator - Far Right */}
          <div className="d-none d-lg-flex ms-3 ps-3" style={{ borderLeft: '1px solid rgba(255,255,255,0.25)' }}>
            <LanguageTranslator />
          </div>

          {/* Mobile Menu Button */}
          <button
            className="navbar-toggler d-lg-none"
            type="button"
            onClick={toggleMobileMenu}
            style={{ border: 'none', background: 'transparent' }}
          >
            {mobileMenuOpen ? <X size={24} color="white" /> : <Menu size={24} color="white" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="d-lg-none bg-dark position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 10000, overflowY: 'auto' }}>
          <div className="container py-3">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center">
                <img src={arcaLogo} alt="ARCA Logo" style={{ height: '40px', width: 'auto' }} className="me-2" />
                <span className="text-white fw-bold">ARCA</span>
              </div>
              <button
                className="btn"
                onClick={toggleMobileMenu}
                style={{ border: 'none', background: 'transparent' }}
              >
                <X size={24} color="white" />
              </button>
            </div>

            <div className="d-flex flex-column">
              {/* Language Translator for Mobile */}
              <div className="mb-3 d-flex justify-content-center">
                <LanguageTranslator />
              </div>

              {mobileMenuItems.map((item, index) => (
                <div key={index} className="mb-2">
                  {item.items ? (
                    <>
                      <button
                        className="btn btn-dark text-white w-100 d-flex justify-content-between align-items-center py-3"
                        onClick={() => toggleMobileDropdown(item.title)}
                        style={{ border: 'none', borderRadius: '0', textAlign: 'left' }}
                      >
                        <div className="d-flex align-items-center">
                          <item.icon size={20} className="me-3" />
                          {item.title}
                        </div>
                        <ChevronDown
                          size={20}
                          style={{ transform: mobileDropdown === item.title ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s' }}
                        />
                      </button>

                      {mobileDropdown === item.title && (
                        <div className="bg-secondary bg-opacity-25">
                          {item.items.map((subItem, subIndex) => (
                            <Link
                              key={subIndex}
                              to={subItem.link}
                              className="d-flex text-white text-decoration-none py-3 px-4 border-bottom border-dark"
                              onClick={toggleMobileMenu}
                            >
                              <subItem.icon size={20} className="me-3 flex-shrink-0" />
                              <div>
                                <div className="fw-bold">{subItem.title}</div>
                                <small className="text-white-50">{subItem.description}</small>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.link}
                      className="btn btn-dark text-white w-100 d-flex align-items-center py-3"
                      style={{ border: 'none', borderRadius: '0', textAlign: 'left' }}
                      onClick={toggleMobileMenu}
                    >
                      <item.icon size={20} className="me-3" />
                      {item.title}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default SecondaryNavbar;